import { useEffect, useState } from 'react';
import axios from 'axios';
import { Activity, RefreshCw, CheckCircle2, XCircle, Trash2, ChevronDown, ChevronRight } from 'lucide-react';

interface LecsIntegration {
  partner_id: number;
  base_url: string;
  enabled: boolean;
  last_sync_at: string | null;
  last_sync_status: 'ok' | 'error' | null;
  last_error: string | null;
  has_api_key: boolean;
}

interface LecsDevice {
  serial_number: string;
  lecs_name: string;
  visible: boolean;
  mapping_id: number | null;
  tenant_id: number | null;
  tenant_name: string | null;
  site_id: number | null;
  site_name: string | null;
  last_event_at: string | null;
  last_sync_at: string | null;
  last_sync_status: 'ok' | 'error' | null;
  last_error: string | null;
}

interface TenantOption { id: number; name: string; }
interface SiteOption { id: number; tenant_id: number; name: string; }

// Integrazione LECS NDR (pull via API REST, vedi backend/src/jobs/lecsSync.ts). A differenza di
// Entra/EDR sopra, la API key LECS ha profilo reseller e quindi vive a livello di PARTNER: il
// pannello è mostrato nel contesto del tenant selezionato ma configura il partner di quel tenant,
// e la tabella dispositivi (discovery da GET /devices) permette di assegnare ogni dispositivo LECS
// a uno qualsiasi dei tenant del partner. Un dispositivo non assegnato non viene sincronizzato.
export default function LecsIntegrationPanel({ tenantId }: { tenantId: number | null }) {
  const [partnerId, setPartnerId] = useState<number | null>(null);
  const [integration, setIntegration] = useState<LecsIntegration | null>(null);
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [sites, setSites] = useState<SiteOption[]>([]);
  const [devices, setDevices] = useState<LecsDevice[]>([]);
  const [discoveryError, setDiscoveryError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const [baseUrl, setBaseUrl] = useState('https://securitylecs.com:5001');
  const [apiKey, setApiKey] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [feedback, setFeedback] = useState<{ ok: boolean; message: string } | null>(null);

  async function loadAll(pid: number) {
    const [integ, tens] = await Promise.all([
      axios.get('/api/lecs-integration', { params: { partnerId: pid } }),
      axios.get('/api/tenants', { params: { partnerId: pid } }),
    ]);
    const data: LecsIntegration | null = integ.data.data;
    setIntegration(data);
    const tenantList: TenantOption[] = tens.data.data.map((t: any) => ({ id: t.id, name: t.name }));
    setTenants(tenantList);
    // Sedi di ogni tenant del partner (Sites & Brokers, stessa tabella): un cliente con più
    // dispositivi LECS in sedi diverse li assegna qui, vedi jobs/lecsSync.ts.
    const siteResults = await Promise.all(tenantList.map(t => axios.get('/api/sites', { params: { tenantId: t.id } })));
    setSites(siteResults.flatMap((res, i) => res.data.data.map((s: any) => ({ id: s.id, tenant_id: tenantList[i].id, name: s.name }))));
    if (data) {
      setBaseUrl(data.base_url);
      setEnabled(data.enabled);
      const dev = await axios.get(`/api/lecs-integration/${pid}/devices`);
      setDevices(dev.data.data.devices);
      setDiscoveryError(dev.data.data.discoveryError);
    } else {
      setDevices([]);
      setDiscoveryError(null);
    }
  }

  useEffect(() => {
    if (!tenantId) return;
    setLoading(true);
    axios.get(`/api/tenants/${tenantId}`)
      .then(res => {
        const pid: number = res.data.data.partner_id;
        setPartnerId(pid);
        return loadAll(pid);
      })
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, [tenantId]);

  if (!tenantId) return null;

  async function reloadDevices() {
    if (!partnerId) return;
    const dev = await axios.get(`/api/lecs-integration/${partnerId}/devices`);
    setDevices(dev.data.data.devices);
    setDiscoveryError(dev.data.data.discoveryError);
  }

  async function save() {
    if (!partnerId) return;
    setSaving(true);
    setFeedback(null);
    try {
      await axios.put('/api/lecs-integration', { partnerId, baseUrl, apiKey: apiKey || undefined, enabled });
      setApiKey('');
      await loadAll(partnerId);
      setFeedback({ ok: true, message: 'Saved.' });
    } catch (err: any) {
      setFeedback({ ok: false, message: err.response?.data?.error || 'Save failed' });
    } finally {
      setSaving(false);
    }
  }

  async function test() {
    if (!partnerId) return;
    setTesting(true);
    setFeedback(null);
    try {
      const res = await axios.post(`/api/lecs-integration/${partnerId}/test`);
      setFeedback({ ok: true, message: `Connection OK: ${res.data.data.deviceCount} device(s) visible with this key.` });
      await reloadDevices();
    } catch (err: any) {
      setFeedback({ ok: false, message: err.response?.data?.error || 'Connection test failed' });
    } finally {
      setTesting(false);
    }
  }

  async function syncNow() {
    if (!partnerId) return;
    setSyncing(true);
    setFeedback(null);
    try {
      const res = await axios.post(`/api/lecs-integration/${partnerId}/sync`);
      const { devices: n, errors } = res.data.data;
      setFeedback({ ok: errors === 0, message: `Sync completed on ${n} device(s)${errors ? `, ${errors} with errors` : ''}.` });
      await loadAll(partnerId);
    } catch (err: any) {
      setFeedback({ ok: false, message: err.response?.data?.error || 'Sync failed' });
    } finally {
      setSyncing(false);
    }
  }

  async function assign(device: LecsDevice, newTenantId: number | null) {
    if (!partnerId) return;
    setFeedback(null);
    try {
      if (newTenantId === null) {
        if (device.mapping_id) await axios.delete(`/api/lecs-integration/${partnerId}/mappings/${device.mapping_id}`);
      } else {
        // Cambiare tenant azzera la sede: una sede del tenant precedente non ha senso su quello
        // nuovo (validato comunque lato server).
        await axios.put(`/api/lecs-integration/${partnerId}/mappings`, {
          serialNumber: device.serial_number, tenantId: newTenantId, lecsName: device.lecs_name,
          siteId: newTenantId === device.tenant_id ? device.site_id : null,
        });
      }
      await reloadDevices();
    } catch (err: any) {
      setFeedback({ ok: false, message: err.response?.data?.error || 'Could not update the mapping' });
    }
  }

  async function assignSite(device: LecsDevice, newSiteId: number | null) {
    if (!partnerId || !device.tenant_id) return;
    setFeedback(null);
    try {
      await axios.put(`/api/lecs-integration/${partnerId}/mappings`, {
        serialNumber: device.serial_number, tenantId: device.tenant_id, lecsName: device.lecs_name, siteId: newSiteId,
      });
      await reloadDevices();
    } catch (err: any) {
      setFeedback({ ok: false, message: err.response?.data?.error || 'Could not update the site' });
    }
  }

  async function remove() {
    if (!partnerId) return;
    if (!window.confirm('Remove the LECS API key and all device mappings of this partner?')) return;
    await axios.delete('/api/lecs-integration', { params: { partnerId } });
    await loadAll(partnerId);
  }

  const mappedHere = devices.filter(d => d.tenant_id === tenantId).length;

  return (
    <div className="card p-4 mb-4">
      <button type="button" onClick={() => setOpen(v => !v)} className="w-full flex items-center justify-between text-left">
        <h2 className="font-medium text-sm text-gray-900 inline-flex items-center gap-2">
          <Activity size={15} /> LECS NDR integration
          {integration && (
            <span className={`badge ${integration.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
              {integration.enabled ? 'Enabled' : 'Disabled'}
            </span>
          )}
          {integration && <span className="text-xs text-gray-400 font-normal">{mappedHere} device(s) mapped to this tenant</span>}
        </h2>
        {open ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />}
      </button>
      <p className="text-xs text-gray-400 mt-1">
        Network Detection & Response by LECS (securitylecs.com). The API key is reseller-level, so it is
        configured once per partner; each LECS device is then assigned to the customer tenant it belongs to.
        Unassigned devices are never imported. A customer with several devices (one per physical location)
        can also assign each device to a Site (managed above, in Sites & Brokers) so cases and assets stay
        distinguishable by location within the same tenant.
      </p>

      {open && (loading ? (
        <p className="text-sm text-gray-400 mt-3">Loading…</p>
      ) : (
        <div className="mt-3 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Base URL</label>
              <input value={baseUrl} onChange={e => setBaseUrl(e.target.value)} className="input-field font-mono text-xs" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">
                API key {integration?.has_api_key && <span className="text-gray-400">(saved — leave blank to keep)</span>}
              </label>
              <input type="password" value={apiKey} onChange={e => setApiKey(e.target.value)}
                className="input-field font-mono text-xs" placeholder={integration?.has_api_key ? '••••••••' : 'Bearer token from LECS'} />
            </div>
            <div className="flex items-center gap-3">
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} /> Enabled
              </label>
              <button onClick={save} disabled={saving || (!integration && !apiKey)} className="btn-primary text-sm">
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button onClick={test} disabled={testing || !integration} className="btn-secondary text-xs inline-flex items-center gap-1">
              <RefreshCw size={12} className={testing ? 'animate-spin' : ''} /> Test connection
            </button>
            <button onClick={syncNow} disabled={syncing || !integration} className="btn-secondary text-xs inline-flex items-center gap-1">
              <RefreshCw size={12} className={syncing ? 'animate-spin' : ''} /> Sync now
            </button>
            {integration && (
              <button onClick={remove} className="text-xs text-gray-400 hover:text-red-500 inline-flex items-center gap-1 ml-auto">
                <Trash2 size={12} /> Remove integration
              </button>
            )}
          </div>

          {feedback && (
            <div className={`text-xs inline-flex items-center gap-1 ${feedback.ok ? 'text-emerald-600' : 'text-red-600'}`}>
              {feedback.ok ? <CheckCircle2 size={14} /> : <XCircle size={14} />} {feedback.message}
            </div>
          )}

          {integration?.last_sync_at && (
            <p className="text-xs text-gray-400">
              Last sync: {new Date(integration.last_sync_at).toLocaleString('en-GB')} —{' '}
              {integration.last_sync_status === 'ok'
                ? <span className="text-emerald-600">ok</span>
                : <span className="text-red-600" title={integration.last_error || ''}>error{integration.last_error ? `: ${integration.last_error}` : ''}</span>}
            </p>
          )}

          {integration && (
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1.5">LECS devices visible with this key</p>
              {discoveryError && <p className="text-xs text-amber-600 mb-2">Device discovery unavailable: {discoveryError}</p>}
              {devices.length === 0 ? (
                <p className="text-xs text-gray-400">No device found.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-gray-500">
                        <th className="py-1 pr-3">Device</th>
                        <th className="py-1 pr-3">Serial</th>
                        <th className="py-1 pr-3">Assigned tenant</th>
                        <th className="py-1 pr-3">Site</th>
                        <th className="py-1 pr-3">Last event</th>
                        <th className="py-1">Sync</th>
                      </tr>
                    </thead>
                    <tbody>
                      {devices.map(d => (
                        <tr key={d.serial_number} className="border-t border-gray-100">
                          <td className="py-1.5 pr-3">
                            {d.lecs_name}
                            {!d.visible && <span className="ml-1 text-amber-600" title="Mapped but no longer listed by LECS">(not visible)</span>}
                          </td>
                          <td className="py-1.5 pr-3 font-mono text-gray-600">{d.serial_number}</td>
                          <td className="py-1.5 pr-3">
                            <select value={d.tenant_id ?? ''} onChange={e => assign(d, e.target.value ? Number(e.target.value) : null)}
                              className="input-field py-1 text-xs w-56">
                              <option value="">— not assigned —</option>
                              {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                            </select>
                          </td>
                          <td className="py-1.5 pr-3">
                            {d.tenant_id ? (
                              <select value={d.site_id ?? ''} onChange={e => assignSite(d, e.target.value ? Number(e.target.value) : null)}
                                className="input-field py-1 text-xs w-40">
                                <option value="">— no site —</option>
                                {sites.filter(s => s.tenant_id === d.tenant_id).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                              </select>
                            ) : <span className="text-gray-300">—</span>}
                          </td>
                          <td className="py-1.5 pr-3 text-gray-500">{d.last_event_at ? new Date(d.last_event_at).toLocaleString('en-GB') : '—'}</td>
                          <td className="py-1.5">
                            {d.last_sync_status === 'ok' && <span className="text-emerald-600" title={d.last_error || ''}>ok{d.last_error ? ' (backlog)' : ''}</span>}
                            {d.last_sync_status === 'error' && <span className="text-red-600" title={d.last_error || ''}>error</span>}
                            {!d.last_sync_status && <span className="text-gray-400">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
